/* El CRT · Carta de Porte Internacional por Carretera.
 *
 * ── DE DÓNDE SALEN LOS CAMPOS ────────────────────────────────────────────────
 *
 * Del CRT real AR.142.042508 (Electrofueguina, 21/08/2026, tránsito
 * 26 001 TRM4 006787 J). Son 24 casillas numeradas y bilingües
 * español/portugués. **Ninguna etiqueta está inventada**: el papel se presenta
 * en la frontera y una casilla mal rotulada es un camión parado.
 *
 * ── ACÁ NO HAY RED ───────────────────────────────────────────────────────────
 *
 * Todo lo de este archivo es cuenta y texto. Lo que habla con la base está en
 * `services/crt.js`. Esa separación es la que permite probar la numeración y
 * los totales sin levantar nada.
 */

/* ── Los cuatro roles de una operación ──────────────────────────────────────
 *
 * En el CRT que miramos, ELECTROFUEGUINA ocupa los cuatro, con DOS CUIT y DOS
 * domicilios distintos según el rol: remitente con el de Buenos Aires,
 * destinatario y consignatario con el de Río Grande. Por eso cada rol elige su
 * ficha por separado y no hay un solo «cliente» del CRT. */
export const ROLES = [
  { id: "remitente", campo: 1, label: "Remitente", pt: "Remetente" },
  { id: "destinatario", campo: 4, label: "Destinatario", pt: "Destinatário" },
  { id: "consignatario", campo: 6, label: "Consignatario", pt: "Consignatário" },
  { id: "notificar", campo: 9, label: "Notificar a", pt: "Notificar a" },
];

/* Las tres filas del campo 15, «Gastos a pagar». Cada una se puede cobrar al
 * remitente o al destinatario — nunca a los dos, pero eso lo decide quien
 * carga, no el sistema. */
export const GASTOS = [
  { id: "flete", label: "Flete", pt: "Frete" },
  { id: "seguro", label: "Seguro", pt: "Seguro" },
  { id: "otros", label: "Otros", pt: "Outros" },
];

export const MONEDA_POR_DEFECTO = "U$S";

/* ── Números a la argentina ────────────────────────────────────────────────
 *
 * El papel dice `23.662,600` y `122.475,37`: punto de miles y coma decimal. Un
 * peso leído con las convenciones al revés da 23,662 kg en vez de 23.662 —
 * un factor de mil en un campo que la aduana controla.
 */

export function aNumero(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = String(v ?? "").trim();
  if (!t) return null;
  // Se sacan los puntos de miles y la coma pasa a punto.
  const limpio = t.replace(/\./g, "").replace(",", ".").replace(/\s/g, "");
  if (!/^-?\d*\.?\d+$/.test(limpio)) return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
}

/* Con cantidad FIJA de decimales, que es como sale impreso: el peso siempre
 * con tres («23.662,600», no «23.662,6») y los valores con dos. Un cero que se
 * cae cambia cómo se lee la cifra. */
export function comoMiles(n, decimales = 2) {
  const v = typeof n === "number" ? n : aNumero(n);
  if (v === null) return "";
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(v);
}

export const comoPeso = (n) => comoMiles(n, 3);

/* Los bultos son cuenta de cosas: enteros y con punto de miles («1.520»). */
export function comoEntero(n) {
  const v = typeof n === "number" ? n : aNumero(n);
  if (v === null) return "";
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(v);
}

/* UN AÑO NO LLEVA PUNTO DE MILES.
 *
 * La casilla 14 del MIC salía «2.026». Es lo que pasa cuando un año se
 * formatea con la misma función que los importes: `Intl` agrupa de a tres y
 * 2026 se vuelve dos mil veintiséis.
 *
 * Es de esos errores que no rompen nada y que en un papel aduanero se leen
 * como un dato mal cargado. Un año es una etiqueta, no una cantidad.
 */
export function comoAnio(n) {
  const v = typeof n === "number" ? n : aNumero(n);
  if (v === null) return "";
  return String(Math.trunc(v));
}

/* ── La numeración ─────────────────────────────────────────────────────────
 *
 * `AR.142.042508`: país, número de idoneidad de la empresa y correlativo. El
 * 142 es de Cantarini y no cambia; lo que corre es el último tramo.
 */

/* LA IDONEIDAD NO SIEMPRE SON TRES DÍGITOS. Era `\d{3}` porque el único
 * talonario cargado era el 142 de Transportes Cantarini. Con las otras tres
 * emisoras (Fabián, 14/09) hay `AR.1270.10898` —cuatro— y `AR.TCC.000009`, que
 * en vez de idoneidad lleva la sigla. Con la forma vieja, `partirNumero`
 * devolvía null para los dos y el aviso de «este número llama la atención»
 * quedaba mudo justo en los talonarios nuevos, que son los que más se van a
 * tipear a mano. */
const FORMA = /^[A-Z]{2}\.[A-Z0-9]{2,5}\.\d+$/;

/* El que sigue, para PROPONER. No lo asigna: lo propone y la persona
 * confirma. Ver el comentario de la migración sobre MEMO TMS. */
export function proximoNumero(serie) {
  if (!serie || !serie.prefijo) return "";
  const ancho = Number(serie.ancho || 3);
  const n = Number(serie.proximo);
  if (!Number.isFinite(n) || n < 0) return "";
  return `${serie.prefijo}${String(n).padStart(ancho, "0")}`;
}

/* ¿El número que está en el campo lo puso el sistema, o lo escribió Fabián?
 *
 * Decide si al cambiar de porteador el número se reemplaza por el del
 * talonario nuevo. Pisarle uno escrito a mano sería peor que dejarlo mal: lo
 * que escribió puede ser el número real del papel que tiene delante.
 *
 * ── SON DOS PROPUESTAS Y HAY QUE MIRAR LAS DOS ───────────────────────────
 *
 * Fabián, 15/09: *«en el borrador modifiqué la empresa y es tomada pero no su
 * numeración»*. Faltaba la segunda.
 *
 *   · `propuestoAhora` — lo que la serie propone en este momento. Sirve para
 *     un CRT nuevo, que se acaba de llenar con ese mismo valor.
 *
 *   · `numero_propuesto` — lo que el bot puso CUANDO CREÓ el borrador. Es el
 *     que hace falta para un borrador guardado: nació con AR.142.042591, se
 *     emitieron otros, y hoy la serie propone AR.142.042593. Comparando sólo
 *     contra el de hoy, el guardado no coincide y el código concluye que lo
 *     escribió una persona. No lo escribió nadie.
 *
 * `guardar()` pone `numero_propuesto` en null en cuanto una persona guarda —«o
 * lo cambió, o lo dejó a sabiendas; las dos cosas son mirarlo»—, así que en
 * cuanto Fabián toca el borrador el número pasa a ser suyo y deja de moverse.
 */
export function elNumeroEsDelSistema(form, propuestoAhora) {
  const n = (form && form.numero) || "";
  if (!n) return true;
  if (n === propuestoAhora) return true;
  return !!(form.numero_propuesto && n === form.numero_propuesto);
}

/* Un número escrito a mano, partido en prefijo y correlativo. Devuelve `null`
 * cuando no tiene la forma de un CRT — y eso NO lo bloquea: sirve para avisar,
 * no para impedir. El día que la serie cambie de forma, un sistema que sólo
 * acepta la vieja deja de poder emitir. */
export function partirNumero(numero, ancho = 3) {
  const t = String(numero ?? "").trim().toUpperCase();
  if (!FORMA.test(t)) return null;

  /* DÓNDE CORTAR LO DECIDE LA SERIE, NO EL NÚMERO.
   *
   * `AR.142.042509` no se puede partir mirándolo: `042` es parte del prefijo
   * y `509` es el correlativo, pero son todos dígitos seguidos y cualquier
   * corte parece razonable. Adivinándolo, el 042509 se leía como el número
   * 42.509 de la serie `AR.142.0` — y entonces «el que sigue» daba cualquier
   * cosa. El ancho lo dice la serie. */
  const a = Number(ancho) || 3;
  const digitos = t.slice(t.lastIndexOf(".") + 1);
  /* MENOS dígitos que el ancho es imposible —el corte se comería el punto—,
   * pero JUSTOS es el caso normal desde que las series guardan el prefijo
   * entero (`AR.287.` + 6) en vez de comerse parte del correlativo
   * (`AR.142.042` + 3). Con `<=` acá, cambiar la serie a la forma limpia
   * dejaba a los CUATRO talonarios sin el aviso de «salta N números»: el
   * número se veía perfecto y se podía emitir salteado sin que nadie lo
   * viera. */
  if (digitos.length < a) return null;
  const corte = t.length - a;
  return { prefijo: t.slice(0, corte), correlativo: Number(t.slice(corte)), ancho: a };
}

/* ── LOS NÚMEROS QUE DEJÓ LIBRES UNA ANULACIÓN ─────────────────────────────
 *
 * Fabián, 08/10: «Puede suceder que el embarque se cancele y necesitaría
 * utilizar el mismo número para el siguiente». Desde la migración
 * `20261008200000_crt_anular_libera_y_partes_se_corrigen` un CRT anulado
 * conserva su número pero no lo ocupa: `crt_numero_unico` mira sólo los vivos.
 *
 * Es la misma regla que `crt_numero_sugerido` en la base (y que
 * `aduana_crt.numero_libre` en el bot): un anulado que SE EMITIÓ, de esta
 * serie, en los `VENTANA_LIBERADOS` números ANTERIORES al `proximo`, y que
 * ningún CRT vivo volvió a tomar. Los recortes no son de adorno: un borrador
 * anulado nunca gastó un número del talonario; uno anulado después del
 * `proximo` es un número que la serie todavía no dio (o uno de fuera del
 * talonario, como el AR.142.043034); y uno muy atrás es casi siempre un
 * número mal tipeado que se anuló por eso (un AR.142.024666 por
 * AR.142.042666) — proponerlo, y sin aviso por ser liberado, lo volvería a
 * emitir.
 *
 * `menos` es el CRT que se está editando: si él mismo tomó el número
 * liberado, ese número sigue siendo «el liberado» para el aviso de abajo. */
export const VENTANA_LIBERADOS = 100;

export function numerosLiberados(crts, serie, { menos = null } = {}) {
  if (!serie || !serie.prefijo) return [];
  const ancho = Number(serie.ancho || 3);
  const tope = Number(serie.proximo);
  const vivos = new Set(
    (crts || [])
      .filter((c) => c && c.estado !== "anulado"
        && (menos === null || String(c.id) !== String(menos)))
      .map((c) => String(c.numero || "").trim()),
  );
  const libres = new Map();
  for (const c of crts || []) {
    if (!c || c.estado !== "anulado" || !c.emitido_en) continue;
    const n = String(c.numero || "").trim();
    if (!n.startsWith(serie.prefijo) || n.length !== serie.prefijo.length + ancho) continue;
    const resto = n.slice(serie.prefijo.length);
    if (!/^\d+$/.test(resto)) continue;
    const corr = Number(resto);
    if (Number.isFinite(tope) && (corr >= tope || corr < tope - VENTANA_LIBERADOS)) continue;
    if (vivos.has(n)) continue;
    libres.set(n, corr);
  }
  return [...libres.entries()].sort((a, b) => a[1] - b[1]).map(([n]) => n);
}

/* El que sigue DE VERDAD en la serie: el `proximo`, salteando los que ya
 * tomó un CRT vivo (un borrador del bot, o uno tipeado a mano). Es el paso 2
 * de `crt_numero_sugerido`. `tomados` son los números de los CRT vivos, SIN
 * el que se está editando. Devuelve el correlativo, o null sin serie. */
export function correlativoQueSigue(serie, tomados = []) {
  if (!serie || !serie.prefijo) return null;
  const ancho = Number(serie.ancho || 3);
  let n = Number(serie.proximo);
  if (!Number.isFinite(n) || n < 0) return null;
  const usados = new Set((tomados || []).map((x) => String(x ?? "").trim().toUpperCase()));
  while (usados.has(`${serie.prefijo}${String(n).padStart(ancho, "0")}`)) n += 1;
  return n;
}

/* El número que se propone, con la cuenta de `crt_numero_sugerido` hecha
 * sobre la lista que ya tiene la pantalla: el liberado más bajo de la
 * ventana y, si no hay, el que sigue salteando los vivos. Es el RESPALDO
 * para cuando la base no contesta: proponer el `proximo` a secas proponía, si
 * el bot ya lo había usado en un borrador, un número que la base rechaza. */
export function numeroSugeridoLocal(crts, serie) {
  if (!serie || !serie.prefijo) return "";
  const libres = numerosLiberados(crts, serie);
  if (libres.length) return libres[0];
  const vivos = (crts || [])
    .filter((c) => c && c.estado !== "anulado")
    .map((c) => c.numero);
  const n = correlativoQueSigue(serie, vivos);
  return n === null ? "" : proximoNumero({ ...serie, proximo: n });
}

/* EL NÚMERO LO OCUPA SÓLO UN EMITIDO (Fabián, ticket 427). Un borrador no
 * reserva nada: el bot los arma con la serie de TC y, si uno sale con otra
 * empresa, su número tiene que poder pasar a otro borrador. Devuelve el CRT
 * emitido que ya tiene ese número (sin contar `menos`), o null. */
export function emitidoConEseNumero(numero, crts, { menos = null } = {}) {
  const t = String(numero ?? "").trim().toUpperCase();
  if (!t) return null;
  return (crts || []).find((c) => c && c.estado === "emitido"
    && (menos === null || String(c.id) !== String(menos))
    && String(c.numero ?? "").trim().toUpperCase() === t) || null;
}

/* «Este número no sigue a la serie.» Un aviso, no un error: puede ser
 * legítimo (un número que MEMO ya usó y hay que saltear). Lo que no puede
 * pasar es que se emita salteado SIN QUE NADIE LO VEA.
 *
 * Lo que NO avisa, porque es justo lo que se propone (avisar contra la propia
 * propuesta enseña a no leer el aviso):
 *
 *   · `liberados`: los que dejó libres una anulación (`numerosLiberados`).
 *     Son anteriores al que sigue a propósito, y reusarlos es lo que se pidió.
 *   · el que sigue SALTEANDO LOS TOMADOS (`tomados`: los números de los CRT
 *     vivos, sin el propio). El bot arma borradores con el `proximo`; si el
 *     que sigue está en un borrador, la base propone el siguiente, y comparar
 *     contra el `proximo` a secas avisaba «salta 1, el que seguía era …» con
 *     un número que ya está usado — y si se le hacía caso, la base lo
 *     rechazaba.
 *   · `sugerido`: el número que propuso la base (`crt_numero_sugerido`).
 *
 * Acepta la lista de liberados suelta como tercer argumento, como antes. */
export function porQueElNumeroLlamaLaAtencion(numero, serie, opciones = {}) {
  const { liberados = [], tomados = [], sugerido = "" } = Array.isArray(opciones)
    ? { liberados: opciones }
    : (opciones || {});
  const t = String(numero ?? "").trim();
  if (!t) return "Falta el número.";
  const partes = partirNumero(t, serie?.ancho);
  if (!partes) return "Ese número no tiene la forma de un CRT (AR.142.042509).";
  if (!serie || !serie.prefijo) return "";
  if (partes.prefijo !== serie.prefijo) {
    return `Ese número es de otra serie: la de acá es ${serie.prefijo}.`;
  }
  const T = t.toUpperCase();
  if ((liberados || []).includes(T)) return "";
  if (sugerido && String(sugerido).trim().toUpperCase() === T) return "";
  const esperado = correlativoQueSigue(serie, tomados);
  if (esperado !== null && partes.correlativo !== esperado) {
    const elQueSeguia = proximoNumero({ ...serie, proximo: esperado });
    return partes.correlativo > esperado
      ? `Salta ${partes.correlativo - esperado} número(s): el que seguía era ${elQueSeguia}.`
      : `Ese número es anterior al que seguía (${elQueSeguia}).`;
  }
  return "";
}

/* ── La copia congelada ────────────────────────────────────────────────────
 *
 * Lo que se imprime, guardado tal cual quedó. Si mañana alguien corrige el
 * domicilio del cliente en el padrón, el CRT que ya viajó tiene que seguir
 * diciendo lo que decía. */
export function congelar(ficha) {
  if (!ficha) return null;
  return {
    id: ficha.id ?? null,
    canonico: ficha.canonico ?? null,
    razon_social: String(ficha.razon_social ?? "").trim(),
    cuit: String(ficha.cuit ?? "").trim(),
    domicilio: String(ficha.domicilio ?? "").trim(),
    localidad: String(ficha.localidad ?? "").trim(),
    provincia: String(ficha.provincia ?? "").trim(),
    pais: String(ficha.pais ?? "").trim() || "ARGENTINA",
    rotulo: String(ficha.rotulo ?? "").trim(),
  };
}

/* ── LA MISMA COLUMNA GUARDA DOS FORMAS, Y ESO TIRABA LO QUE EL BOT LEYÓ ───
 *
 * Igna, sobre el CRT AR.142.043007: *«no hiciste el 80% del CRT. ¿Por qué no
 * completás las partes que faltan? ¿No tenés esos datos del ARCA ni el BL?»*.
 *
 * Los teníamos. `aduana_crt.desde_embarque` lee los cuatro roles de los
 * papeles y los guarda en las columnas `remitente`, `destinatario`,
 * `consignatario` y `notificar`. El 043007 tenía adentro, desde el primer
 * día:
 *
 *     remitente     TCL GLOBAL MARKETING COMPANY LIMITED (Hong Kong)
 *     destinatario  RADIO VICTORIA ARGENTINA S.A. · 30-58481198-2
 *
 * Y la pantalla mostraba las cuatro casillas VACÍAS. `partesDe` resolvía cada
 * rol únicamente por `<rol>_id` —la ficha elegida del padrón fiscal— y un
 * borrador que armó el bot nunca tiene ese id, porque elegir la ficha es de
 * Fabián: poner un id adivinado sería firmar un CUIT. Lo que no estaba
 * decidido es que, además de no elegir la ficha, se tirara el texto.
 *
 * ── LAS DOS FORMAS ────────────────────────────────────────────────────────
 *
 *     el bot          {nombre, cuit, domicilio}
 *     `crt_emitir`    {razon_social, cuit, domicilio, localidad, provincia,
 *                      pais, rotulo, id, canonico}
 *
 * `nombre` contra `razon_social` es toda la diferencia: `comoBloque` lee la
 * segunda, así que sobre lo que leyó el bot devolvía un bloque sin nombre.
 *
 * ── Y EL PAÍS NO SE COMPLETA ──────────────────────────────────────────────
 *
 * `congelar` pone «ARGENTINA» cuando falta, y ahí está bien: son fichas del
 * padrón fiscal argentino. Acá NO. El remitente del 043007 es de Hong Kong, y
 * rellenar el país con ARGENTINA imprimiría un dato falso en la casilla 1 de
 * un papel que se presenta en la frontera — creíble y equivocado, que es la
 * peor combinación. Sin país, la casilla lo omite. */
export function comoParte(v) {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const t = (x) => String(x ?? "").trim();
  const razon = t(v.razon_social) || t(v.nombre);
  const domicilio = t(v.domicilio);
  // Sin nombre NI domicilio no hay parte: un objeto con las claves en blanco
  // dibujaría una casilla que dice que se sabe algo cuando no se sabe nada.
  if (!razon && !domicilio) return null;
  return {
    ...v,
    razon_social: razon,
    domicilio,
    localidad: t(v.localidad),
    provincia: t(v.provincia),
    pais: t(v.pais),
    cuit: t(v.cuit),
  };
}

/* Quién va en una casilla: la ficha elegida si la hay, y si no lo que se leyó
 * del papel.
 *
 * EN ESE ORDEN Y NO AL REVÉS. La ficha del padrón la eligió una persona y
 * trae el CUIT y el domicilio fiscal que la empresa usa para facturar; lo del
 * papel es lo que el despachante escribió. Cuando las dos están, manda la que
 * alguien miró. */
export function parteDe(crt, rol, elegida = null) {
  return elegida || comoParte(crt?.[rol]);
}

/* El bloque de líneas como sale impreso en las casillas 1, 3, 4, 6 y 9. */
export function comoBloque(parte) {
  if (!parte) return [];
  const donde = [parte.localidad, parte.provincia]
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .join(" - ");
  return [
    String(parte.razon_social ?? "").trim(),
    String(parte.domicilio ?? "").trim(),
    donde,
    String(parte.pais ?? "").trim(),
    parte.cuit ? `CUIT: ${parte.cuit}` : "",
  ].filter(Boolean);
}

/* ── Qué falta antes de emitir ─────────────────────────────────────────────
 *
 * Nombra lo que falta. «Revisá los campos» sobre un formulario de 24 casillas
 * es lo mismo que no decir nada.
 *
 * Lo exigido es lo que el papel no puede salir sin decir: quién manda, quién
 * recibe, qué es y cuánto pesa. El resto (consignatario, notificar, seguro,
 * observaciones) sale en blanco en muchos CRT reales, y forzarlo sería
 * inventar un requisito que la aduana no pide.
 */
/* Cada campo obligatorio se dice de DOS formas y sabe en qué casilla vive.
 *
 * `label` es para una frase — «Faltan el remitente, el destinatario y la fecha
 * de la carga.» —; `corto` es para una lista, donde los artículos son ruido
 * repetido diez veces. Son el mismo dato dicho para dos lugares distintos, y
 * tenerlos juntos acá es lo que evita que uno diga una cosa y el otro otra.
 *
 * `casilla` es el número impreso en la hoja. Es lo que permite pintar de rojo
 * la casilla vacía sin que la hoja tenga que saber qué es obligatorio: la
 * hoja pregunta «¿la 7 está en falta?» y acá está la respuesta. Dos campos
 * pueden compartir casilla —el lugar y la fecha de carga son los dos la 7— y
 * eso está bien: la casilla se pinta una sola vez. */
export const PARA_EMITIR = [
  { campo: "numero", label: "el número", corto: "número", casilla: 2 },
  { campo: "transportista_id", label: "el porteador", corto: "porteador", casilla: 3 },
  { campo: "remitente_id", label: "el remitente", corto: "remitente", casilla: 1 },
  { campo: "destinatario_id", label: "el destinatario", corto: "destinatario", casilla: 4 },
  { campo: "carga_lugar", label: "el lugar donde se toma la carga", corto: "lugar de carga", casilla: 7 },
  { campo: "carga_fecha", label: "la fecha de la carga", corto: "fecha de carga", casilla: 7 },
  { campo: "entrega_lugar", label: "el lugar de entrega", corto: "lugar de entrega", casilla: 8 },
  { campo: "bultos", label: "la cantidad de bultos", corto: "bultos", casilla: 11 },
  { campo: "mercaderia", label: "qué es la mercadería", corto: "mercadería", casilla: 11 },
  { campo: "peso_bruto", label: "el peso bruto", corto: "peso bruto", casilla: 12 },
];

/* ── El número que propuso el bot todavía no lo miró nadie ─────────────────
 *
 * El borrador que arma `aduana_crt.py` nace con el número que sigue en la
 * serie: real, correlativo y sin colisión. Pero un número REAL no es un número
 * CONFIRMADO — hasta que Fabián lo vea, es lo que el bot supuso que tocaba, y
 * el que va impreso en un papel que se presenta en la frontera lo elige una
 * persona.
 *
 * NO SE PUEDE SABER MIRANDO `numero` SOLO: «lo dejó porque está bien» y «no lo
 * miró» son el mismo valor. Por eso la propuesta se guarda aparte, en
 * `numero_propuesto`, y esto compara. Cambiarlo o confirmarlo la deja en null,
 * y ahí el CRT se comporta como cualquier otro. */
export function numeroSinMirar(crt) {
  const prop = String(crt?.numero_propuesto || "").trim();
  if (!prop) return false;
  return String(crt?.numero || "").trim() === prop;
}

/* ── Los campos que completó el bot todavía no los miró nadie ──────────────
 *
 * Es `numeroSinMirar` para el resto del formulario. El bot escribe las
 * partes, los lugares y la fecha (Igna, 31/08: «el bot tiene que hacer TODO
 * lo que se pueda hacer, FABIÁN lo valida») y cada campo queda anotado en
 * `propuesto` con su motivo; guardar lo limpia, porque guardar es mirarlo.
 *
 * NO SE PUEDE DEDUCIR DE `numero_propuesto`: los borradores viejos —los
 * anteriores a que existiera la marca del número— la tienen en null, y cuando
 * la pasada del bot los complete quedarían VERDES con seis campos que nadie
 * cotejó. Verde es «Fabián sólo valida», no «nadie miró». */
export function elegidoSinMirar(crt) {
  return Object.keys(crt?.propuesto || {}).length > 0;
}

/* Cómo se nombra cada campo que el bot puede haber puesto, para un aviso que
 * se lee corrido («el remitente, la fecha de carga…»). */
const NOMBRE_PUESTO = {
  remitente_id: "el remitente",
  destinatario_id: "el destinatario",
  consignatario_id: "el consignatario",
  notificar_id: "a quién notificar",
  carga_lugar: "el lugar de carga",
  emision_lugar: "el lugar de emisión",
  carga_fecha: "la fecha de carga",
  mercaderia: "la mercadería",
};

/* Qué campos obligatorios faltan, en una lista.
 *
 * SE EXPONE PARA QUE EL SEMÁFORO NO TENGA QUE DEDUCIRLO DEL TEXTO. Antes
 * `queFaltaParaEmitir` devolvía una frase y el semáforo miraba otra cosa para
 * decidir; con dos caminos distintos hacia la misma pregunta, uno se
 * equivocó. Ahora los dos salen de acá. */
export function faltantes(crt) {
  return PARA_EMITIR.filter((c) => {
    const v = crt?.[c.campo];
    if (v === null || v === undefined) return true;
    return String(v).trim() === "";
  });
}

export function camposQueFaltan(crt) {
  return faltantes(crt).map((c) => c.label);
}

/* Lo mismo, en corto y para una lista: «remitente; destinatario; fecha de
 * carga». Es lo que le falta a la fila roja de la lista para que no haya que
 * abrir el CRT para enterarse de por qué está en rojo. */
export function loQueFalta(crt) {
  return faltantes(crt).map((c) => c.corto);
}

/* Las casillas de la hoja que están vacías y no pueden estarlo. Sin repetir:
 * la 7 tiene dos campos obligatorios y se pinta una vez. */
export function casillasQueFaltan(crt) {
  return [...new Set(faltantes(crt).map((c) => c.casilla))];
}

export function queFaltaParaEmitir(crt) {
  const falta = camposQueFaltan(crt);

  // VA PRIMERO Y SOLO. Un número sin mirar no es «un campo más que falta»: es
  // lo único que hay que hacer antes de emitir, y mezclarlo en una lista de
  // seis cosas es la forma más segura de que se pase por alto.
  if (falta.length === 0 && numeroSinMirar(crt)) {
    return "Confirmá el número: el que está lo propuso el bot.";
  }

  if (falta.length === 0) return "";
  if (falta.length === 1) return `Falta ${falta[0]}.`;
  return `Faltan ${falta.slice(0, -1).join(", ")} y ${falta[falta.length - 1]}.`;
}

/* ── Lo que una persona le corrigió al bot ─────────────────────────────────
 *
 * Igna: «edita lo que tenga que editar (APRENDEMOS DE ESE EDIT)».
 *
 * ARRANCA COMO REGISTRO Y NO COMO APRENDIZAJE, y la diferencia importa. Que el
 * bot cambie lo que propone en base a esto es una decisión que necesita estos
 * datos primero: aprender de una sola corrección es exactamente cómo se
 * empieza a inventar. Lo que se guarda hoy es la materia prima — y ya sirve
 * sola para contestar «¿qué le tuvimos que arreglar al bot este mes?».
 *
 * SE COMPARA COMO TEXTO, y no es pereza: `1246` y `"1246"` son el mismo dato
 * escrito por dos caminos (uno vino de la base, el otro de un `<input>`), y
 * anotarlos como una corrección llenaría el registro de ediciones que nadie
 * hizo. Lo que interesa es qué DECÍA y qué DICE.
 *
 * Vacío y null también son lo mismo acá, por el mismo motivo. */
export function comoTexto(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).trim();
}

/* Los campos cuyo cambio se anota. Es la lista de lo que el bot PUEDE haber
 * propuesto mal, no todo lo editable: los gastos y las firmas los pone siempre
 * una persona, así que anotarlos sería registrar tipeo, no corrección.
 *
 * ── LOS SEIS QUE ENTRARON EL 31/08 ────────────────────────────────────────
 *
 * Las cuatro partes, la fecha y el lugar de emisión estaban afuera con este
 * argumento: «los pone siempre una persona». Dejó de ser cierto el día que el
 * bot empezó a proponerlos —los seis— y entonces cambiarlos SÍ es corregirle
 * algo. `carga_fecha` es el que más interesa de todos: es la propuesta más
 * floja que hace el bot (es la de hoy, no sale de ningún papel) y esta lista es
 * la única forma de saber cuántas veces acierta.
 *
 * `numero` NO está: que Fabián lo cambie es el flujo normal y esperado, no un
 * error del bot. Lo que sí queda anotado de él es que lo confirmó, y eso lo
 * dice `numero_propuesto` quedando en null. */
export const EDITABLES_PARA_DIFF = [
  "remitente_id",
  "destinatario_id",
  "consignatario_id",
  "notificar_id",
  "mercaderia",
  "bultos",
  "bultos_clase",
  "peso_bruto",
  "peso_neto",
  "volumen",
  "emision_lugar",
  "carga_lugar",
  "carga_fecha",
  "entrega_lugar",
  "referencia",
  "documentos_anexos",
];

/* ── Lo que un CRT emitido NO deja corregir ────────────────────────────────
 *
 * Fabián, 06/10: «como hicimos con el MIC, permitir hacer correcciones en el
 * CRT una vez que pasamos a estado EMITIDO». Se corrige todo menos lo que hace
 * que sea ESE papel y no otro: el número y la empresa que lo emitió. Para
 * cambiar eso sigue siendo anular y emitir otro.
 *
 * LAS PARTES SALIERON DE ACÁ EL 08/10. Fabián: «me dejes hacer el 100% de las
 * correcciones, incluido los campos 1,4,6,9». Se elige otra ficha y la base
 * vuelve a congelar la copia (`crt.remitente`, …) leyendo `cliente_fiscal`,
 * como al emitir; la pantalla sigue sin mandar copias. Ver la migración
 * `20261008200000_crt_anular_libera_y_partes_se_corrigen`.
 *
 * Es la misma lista que mira el trigger `crt_congelado` en la base, que además
 * cuida la copia congelada del porteador. Acá está para dos cosas: que la
 * pantalla bloquee esos campos en vez de dejar tipearlos y que la base los
 * rechace después, y que `guardar` no los mande. */
export const FIJOS_SI_EMITIDO = [
  "numero",
  "transportista_id",
];

/* El 1 y el 4 se corrigen, pero no se vacían: un CRT emitido sin remitente o
 * sin destinatario no es un papel que se pueda presentar (la base lo frena
 * con `crt_emitido_congelado`). El 6 y el 9 sí: muchos CRT reales salen sin
 * consignatario ni a quién notificar. Devuelve el aviso, o "". */
export function parteVaciaEnEmitido(crt) {
  if (crt?.estado !== "emitido") return "";
  const vacia = (k) => String(crt?.[k] ?? "").trim() === "";
  const faltan = [
    vacia("remitente_id") ? "el remitente (1)" : "",
    vacia("destinatario_id") ? "el destinatario (4)" : "",
  ].filter(Boolean);
  if (!faltan.length) return "";
  return `En un CRT emitido ${faltan.join(" y ")} se puede${faltan.length > 1 ? "n" : ""} `
    + "cambiar por otra ficha, pero no dejar vacío.";
}

/* La hoja de un emitido mientras se lo corrige.
 *
 * La hoja de un emitido dibuja la COPIA congelada, y la copia la rehace la base
 * recién al guardar. Sin esto, elegir otro remitente en el desplegable no
 * cambiaba nada en la vista previa hasta guardar y volver a abrir — y una
 * vista previa que no muestra lo que se está por guardar enseña a no mirarla.
 *
 * Si la ficha elegida (`<rol>_id`) ya no es la de la copia, la hoja muestra la
 * ficha tal cual está en el padrón, que es lo que la base va a congelar. Lo
 * que no se tocó se queda con su copia. */
export function conLasPartesElegidas(crt, fichaPorId) {
  if (!crt || crt.estado !== "emitido") return crt;
  let cambio = false;
  const hoja = { ...crt };
  for (const r of ROLES) {
    const elegida = String(crt[`${r.id}_id`] ?? "");
    const copia = String(crt[r.id]?.id ?? "");
    if (elegida === copia) continue;
    hoja[r.id] = elegida ? (fichaPorId ? fichaPorId(elegida) : null) || crt[r.id] : null;
    cambio = true;
  }
  return cambio ? hoja : crt;
}

/* Qué le cambió una persona al bot, campo por campo.
 *
 * `comoSeLee` traduce un valor a algo que se entienda dentro de un año.
 * SIN ESO LAS CUATRO PARTES SON INÚTILES: «destinatario_id: 11 → 10» no dice
 * nada, y el día que alguien lea el registro para entender qué falla, un id
 * suelto es exactamente lo mismo que no haberlo anotado. Se le pasa la tabla
 * de fichas desde la pantalla, que ya la tiene cargada.
 *
 * `porque` es el motivo con el que el bot había propuesto lo que estaba antes.
 * Es la mitad que convierte esto en un registro de CORRECCIONES: sin él se
 * sabe qué cambió, con él se sabe qué regla se equivocó. */
export function ediciones(antes, despues, campos = EDITABLES_PARA_DIFF,
                          { comoSeLee, porque } = {}) {
  const leer = (campo, v) =>
    (comoSeLee ? comoSeLee(campo, v) : null) ?? comoTexto(v);
  const filas = [];
  for (const campo of campos) {
    const a = comoTexto(antes?.[campo]);
    const d = comoTexto(despues?.[campo]);
    if (a === d) continue;
    filas.push({
      campo,
      antes: a ? leer(campo, antes?.[campo]) : null,
      despues: d ? leer(campo, despues?.[campo]) : null,
      porque: (porque || {})[campo] || null,
    });
  }
  return filas;
}

/* ── Los totales del campo 15 ──────────────────────────────────────────────
 *
 * La fila de abajo del papel es la suma de las tres. Se calcula, no se tipea:
 * es la primera de las cuentas de este circuito que hoy lleva una persona. */
export function totalGastos(gastos, quien) {
  const g = gastos || {};
  let hay = false;
  let total = 0;
  for (const fila of GASTOS) {
    // De máquina primero: los gastos salen de inputs `type="number"` y se
    // guardan en el jsonb tal cual, con punto decimal — ver `unNumeroDeLaHoja`.
    const v = unNumeroDeLaHoja(g?.[fila.id]?.[quien]);
    if (v === null) continue;
    hay = true;
    total += v;
  }
  return hay ? total : null;
}

/* La moneda de una columna: la primera que se haya escrito, y `U$S` si no hay
 * ninguna. Todas las filas de una columna van en la misma. */
export function monedaDe(gastos, quien) {
  const g = gastos || {};
  for (const fila of GASTOS) {
    const m = String(g?.[fila.id]?.[`${quien}_moneda`] ?? "").trim();
    if (m) return m;
  }
  return MONEDA_POR_DEFECTO;
}

/* ── Cómo se lista ─────────────────────────────────────────────────────────
 *
 * LOS EMITIDOS PRIMERO. Estaban al revés —los borradores arriba, por ser «el
 * trabajo pendiente»— y Fabián, que es quien usa la pantalla, pidió lo
 * contrario el 14/09: *«dejarlos en las últimas filas, dejando los emitidos en
 * primera fila»*.
 *
 * Tiene sentido con lo que hay adelante: un CRT emitido es un papel que existe
 * y que se vuelve a buscar —para el MIC, para la carpeta del viaje, para
 * cotejar un número—, y los borradores son treinta y pico de la etapa de
 * prueba que no son trabajo de nadie. Dentro de cada estado, el más nuevo
 * arriba. */
const ORDEN_ESTADO = { emitido: 0, borrador: 1, anulado: 2 };

export function ordenar(lista) {
  return [...(lista || [])].sort((a, b) => {
    const e = (ORDEN_ESTADO[a.estado] ?? 9) - (ORDEN_ESTADO[b.estado] ?? 9);
    if (e !== 0) return e;
    return String(b.creado_en || "").localeCompare(String(a.creado_en || ""));
  });
}

/* Una línea para la lista: qué es este CRT sin tener que abrirlo. */
export function comoSeLee(crt) {
  if (!crt) return "";
  const partes = [
    crt.remitente?.razon_social || "",
    crt.destinatario?.razon_social || "",
  ].filter(Boolean);
  const quien = partes.length === 2 ? partes.join(" → ") : partes[0] || "";
  const carga = [
    crt.bultos ? `${comoEntero(unNumeroDeLaHoja(crt.bultos))} ${crt.bultos_clase || "bultos"}` : "",
    crt.peso_bruto ? `${comoPeso(unNumeroDeLaHoja(crt.peso_bruto))} kg` : "",
  ].filter(Boolean).join(" · ");
  return [quien, carga].filter(Boolean).join(" — ");
}

/* ── LO QUE LOS PAPELES DEJAN EN DUDA ──────────────────────────────────────
 *
 * Igna: *«ampliá el amarillo: lectura dudosa y peso que no cierra»*.
 *
 * El semáforo hasta acá miraba SÓLO el CRT: qué campos le faltan y si el
 * número lo confirmó alguien. Pero el borrador no se escribió a mano — lo armó
 * `aduana_crt.py` leyendo el tránsito de ARCA y el BL. Un CRT puede estar
 * completo, con todos los campos llenos, y estar construido sobre una lectura
 * que el modelo mismo marcó como dudosa. Eso hoy salía en verde.
 *
 * ── `problema` NO ES UNA SEÑAL, Y ESTO SE MIDIÓ ───────────────────────────
 *
 * Parecía el campo obvio: se llama «problema» y lo escribe el que leyó. Pero
 * viene lleno en LOS DIECIOCHO papeles archivados, incluidos los doce que se
 * leyeron perfecto. No dice «algo salió mal»: dice qué había en el papel que
 * no entró en los campos —que el BL trae contenedores donde el CRT quiere
 * bultos, que el peso neto está por ítem y no totalizado, que la carátula
 * remite a un anexo—. Es una nota al pie, y es valiosa; pero como semáforo
 * pintaría de amarillo todo, que es exactamente el error que se acaba de
 * arreglar del otro lado.
 *
 * Las que sí discriminan son tres, y las tres significan «no me fío de lo que
 * leí»: que la lectura fallara entera (`lectura_error`), que el que leyó dijera
 * que no está seguro (`seguro: false`), y que haya números impresos que no se
 * pudieron convertir (`ilegibles`).
 *
 * ── Y EL PESO, QUE ES OTRA COSA ───────────────────────────────────────────
 *
 * El tránsito y el BL declaran EL MISMO PESO —Igna: *«Es el mismo peso. El
 * tránsito lo emite la aduana argentina y el BL un naviero»*—. Así que si no
 * coinciden, uno de los dos se leyó mal, y no hay forma de saber cuál desde acá.
 *
 * Lo que hace esto peligroso es la convención: el BL imprime
 * `37,255.4000 KGS` y el tránsito `37.255,400`. Son el mismo número escrito al
 * revés, y leído con la convención cambiada da un factor de MIL en la casilla
 * que la aduana controla. Hoy los seis embarques cierran al gramo; el día que
 * uno no cierre, tiene que verse.
 */

/* ── Y ESTE NÚMERO NO SE LEE CON `aNumero` ─────────────────────────────────
 *
 * `aNumero` lee lo que ESCRIBE UNA PERSONA: convención argentina, punto de
 * miles y coma decimal. `aNumero("37255.4")` da 372.554 — y tiene razón, es
 * cómo se escribe acá.
 *
 * Pero el peso que devuelve la lectura del papel ya viene normalizado por
 * `aduana_lectura`: es un número de máquina, con punto decimal y sin
 * separadores, venga como número (`->` sobre el jsonb) o como texto (`->>`,
 * que devuelve texto siempre). Pasarlo por `aNumero` lo multiplica por mil.
 *
 * Lo agarró el test de una línea, y es justo la clase de error que este
 * chequeo existe para encontrar: dos convenciones de escribir el mismo número,
 * y un factor de mil entre las dos. */
export function unNumeroDeMaquina(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = String(v ?? "").trim();
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/* ── LOS NÚMEROS DE LA HOJA DEL CRT ────────────────────────────────────────
 *
 * La hoja (`CrtPapel`) dibuja dos cosas distintas con los mismos campos: el
 * CRT que volvió de la base —números de verdad— y, en la vista previa del
 * formulario, lo que está escrito AHORA en los inputs. Y un input
 * `type="number"` devuelve texto con punto decimal: «122475.37». Es un número
 * de máquina, igual que el de la base, sólo que en texto.
 *
 * Los formateadores (`comoMiles`, `comoPeso`, `comoEntero`) leen el texto con
 * `aNumero`, que es convención argentina, y ahí estaba el agujero: «122475.37»
 * recién tipeado en la 14 salía 12.247.537,00 en la vista previa —el punto
 * leído como de miles—, y se arreglaba solo al guardar, porque la base lo
 * devolvía como número. Desde que la 16 copia la 14, salía mal en las dos.
 * Lo mismo con los pesos, el volumen, los gastos del 15 y el flete externo.
 *
 * Así que todo número de la hoja se lee primero como número de máquina. Sólo
 * si NO lo es —«250,50», con coma, que no sale de ningún input numérico pero
 * puede estar en un `gastos` viejo o en un test— se cae a `aNumero`. La
 * ambigüedad de verdad es «1.520»: de máquina es uno coma cinco dos, a la
 * argentina es mil quinientos veinte. Se resuelve como máquina porque es de
 * donde vienen estos campos —el input o la base—; un texto a la argentina con
 * punto de miles y sin coma no lo produce nadie en este circuito. */
export function unNumeroDeLaHoja(v) {
  const n = unNumeroDeMaquina(v);
  return n !== null ? n : aNumero(v);
}

/* Un kilo. Los pesos vienen con tres decimales, así que una diferencia de
 * gramos es redondeo y no discrepancia; arriba del kilo, dos papeles que
 * hablan de la misma carga ya no dicen lo mismo. */
export const TOLERANCIA_PESO_KG = 1;

const NOMBRE_PAPEL = { arca: "el tránsito", bl: "el BL", crt: "el CRT" };
const comoPapel = (tipo) => NOMBRE_PAPEL[String(tipo || "").trim()] || "un papel";

/* `seguro` puede llegar como booleano (de un objeto en memoria) o como el texto
 * `"false"` (de PostgREST, que devuelve `->>` siempre como texto). Ausente NO
 * es dudoso: un papel que todavía no se leyó no es un papel mal leído. */
function dijoQueNoEstaSeguro(v) {
  if (v === false) return true;
  return String(v ?? "").trim().toLowerCase() === "false";
}

const comoLista = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

/* Las dudas que dejan los papeles de los que salió este CRT, en frases.
 *
 * DEVUELVE TEXTO Y NO UN BOOLEANO a propósito. «Algo no cuadra» sobre un CRT
 * de 24 casillas es lo mismo que no decir nada: el que lo mira tiene que saber
 * qué mirar. Es el mismo criterio que `camposQueFaltan`.
 *
 * Sin papeles devuelve vacío, y eso es lo correcto: un CRT cargado a mano no
 * tiene lectura de la que dudar. */
export function revisarPapeles(crt, papeles) {
  const lista = Array.isArray(papeles) ? papeles.filter(Boolean) : [];
  if (lista.length === 0) return [];

  const dudas = [];

  // ── 1. La lectura ───────────────────────────────────────────────────────
  for (const p of lista) {
    const cual = comoPapel(p.tipo);
    if (String(p.lectura_error || "").trim()) {
      dudas.push(`No se pudo leer ${cual}.`);
      continue; // Sin lectura no hay nada más que dudar de este papel.
    }
    if (dijoQueNoEstaSeguro(p.seguro)) {
      dudas.push(`La lectura de ${cual} quedó dudosa.`);
    }
    const ilegibles = comoLista(p.ilegibles);
    if (ilegibles.length > 0) {
      dudas.push(
        `En ${cual} hay números que no se pudieron leer: ${ilegibles.join(", ")}.`
      );
    }
  }

  // ── 2. El peso ──────────────────────────────────────────────────────────
  const pesos = [];
  for (const p of lista) {
    const n = unNumeroDeMaquina(p.peso_bruto_leido);
    if (n === null) continue;
    pesos.push({ que: comoPapel(p.tipo), kg: n });
  }

  const noCierra = (a, b) => Math.abs(a - b) > TOLERANCIA_PESO_KG;
  const kg = (n) => `${comoPeso(n)} kg`;

  // Papel contra papel. Se compara cada uno con el primero: alcanza para que
  // cualquier desacuerdo aparezca, y no repite la misma pareja dos veces.
  const primero = pesos[0];
  if (primero) {
    for (const otro of pesos.slice(1)) {
      if (noCierra(primero.kg, otro.kg)) {
        dudas.push(
          `El peso no cierra: ${primero.que} dice ${kg(primero.kg)} y ` +
          `${otro.que} dice ${kg(otro.kg)}.`
        );
      }
    }
  }

  // El CRT contra los papeles. Puede diferir porque alguien lo corrigió a mano
  // —y puede tener razón—, pero que se emita un peso que no está en ningún
  // papel del embarque no puede pasar sin que nadie lo vea.
  /* El peso del CRT TAMBIÉN es de máquina, y no era obvio: el campo se tipea
   * a mano en el formulario, así que parecía territorio de `aNumero`. Pero
   * `paraLaBase` lo guarda con `Number(v)` — o sea que lo que hay en la base,
   * lo que vuelve en la lista y lo que está en el input son todos la misma
   * convención, con punto decimal. Leerlo a la argentina acá habría inventado
   * un factor de mil en el chequeo que existe para detectar factores de mil. */
  const delCrt = unNumeroDeMaquina(crt?.peso_bruto);
  if (delCrt !== null && pesos.length > 0) {
    const coincide = pesos.some((p) => !noCierra(delCrt, p.kg));
    if (!coincide) {
      const dicen = pesos.map((p) => `${p.que} ${kg(p.kg)}`).join(" y ");
      dudas.push(`El CRT dice ${kg(delCrt)} y ${dicen}.`);
    }
  }

  return dudas;
}

/* ── El semáforo del CRT ───────────────────────────────────────────────────
 *
 * Igna, sobre la lista de `/aduana/crt`: *«acá teníamos que poner el semáforo
 * indicando si nos queda algo pendiente de ese CRT, si está listo para que lo
 * revise, o si nos falta algo/algo está mal»*.
 *
 * ── NO ES LA MISMA BANDERA QUE LA DE LOS PAPELES, Y NO PUEDE SERLO ────────
 *
 * Se ven igual y contestan preguntas distintas. La de `/aduana/papeles`
 * mira si LLEGARON los dos papeles del embarque. Ésta mira si el CRT que se
 * armó con ellos está en condiciones de emitirse. Un embarque verde —los dos
 * papeles leídos— puede tener un CRT rojo, porque el bot no llena los campos
 * que no salen del papel.
 *
 * Las etiquetas se comparten a propósito: son las tres mismas palabras que la
 * gente ya viene leyendo en la otra pantalla, y el `title` de cada una dice lo
 * que significa acá.
 *
 * ── SALE DE `queFaltaParaEmitir` Y NO DE UNA REGLA PROPIA ────────────────
 *
 * Es lo mismo que decide si el botón de emitir se puede apretar. Con una regla
 * aparte, el día que cambie una habría un CRT en verde con el botón bloqueado
 * —o peor, uno en rojo que igual se emite— y las dos pantallas se verían
 * igual de creíbles. */
export const SEMAFORO_CRT = {
  verde: {
    icono: "🟢",
    label: "Listo",
    que: "No le falta nada: está para revisar y emitir",
  },
  amarillo: {
    icono: "🟡",
    label: "Stand by",
    que:
      "No le falta nada, pero hay algo que una persona tiene que mirar antes " +
      "de emitir: el número que propuso el bot, una lectura dudosa o un peso " +
      "que no cierra entre los papeles",
  },
  rojo: {
    icono: "🔴",
    label: "Mirar",
    que: "Le falta algún dato obligatorio para poder emitirse",
  },
};

/* El color de un CRT, o `null` si no lleva.
 *
 * SÓLO LOS BORRADORES. Un CRT emitido ya salió y uno anulado ya no va a
 * salir: el semáforo es para saber qué queda por hacer, y ponerle uno a algo
 * terminado convierte la lista en un árbol de navidad donde el que hay que
 * mirar de verdad no se distingue. El chip de estado ya dice qué son.
 */
export function semaforoCrt(crt, papeles) {
  if (String(crt?.estado || "").trim() !== "borrador") return null;

  /* ── AMARILLO ES SÓLO CUANDO NO FALTA NINGÚN CAMPO ────────────────────────
   *
   * La versión anterior decía `numeroSinMirar(crt) ? "amarillo" : "rojo"`, con
   * un comentario que explicaba —correctamente— que el número sin confirmar es
   * lo único que `queFaltaParaEmitir` reporta cuando ya no falta nada. Pero el
   * código no lo comprobaba: alcanzaba con que el número estuviera sin mirar
   * para dar amarillo, aunque faltaran seis campos.
   *
   * Y los borradores nacen así, con el número que propone el bot. O sea que
   * **TODOS salían en Stand by**, incluidos los seis que hoy no tienen
   * remitente, destinatario ni fecha de carga. Lo vio Igna en la lista: cuatro
   * CRT seguidos en amarillo, ninguno emitible.
   *
   * Es el peor error que puede cometer un semáforo: pintar de «esperando» algo
   * que está roto. El que mira una fila amarilla la deja para después.
   */
  const faltanCampos = camposQueFaltan(crt);
  if (faltanCampos.length > 0) return "rojo";

  /* ── LO QUE DEJAN EN DUDA LOS PAPELES TAMBIÉN ES AMARILLO ────────────────
   *
   * Y NO ES ROJO. Rojo es «falta un dato»: se ve solo, lo arregla quien
   * carga, y el botón de emitir está bloqueado hasta que aparezca. Esto es
   * otra cosa — el dato está, viene de un papel, y lo que hay que hacer es
   * MIRARLO contra el papel. Bloquearlo dejaría a Fabián sin poder emitir un
   * CRT correcto porque el modelo dudó; ignorarlo dejaría salir un peso leído
   * al revés. Amarillo es exactamente eso: se emite, pero no sin mirar. */
  if (revisarPapeles(crt, papeles).length > 0) return "amarillo";

  /* ── EL SEMÁFORO HABLA DE CERTEZA (Igna, 31/08/2026) ─────────────────────
   *
   * «VERDE: si está bien lo que el bot trae de los archivos, el CRT está
   * perfecto — falta que Fabián lo valide, nada más. AMARILLO es para cuando
   * el bot no está seguro de lo que está poniendo y se necesita que Fabián
   * preste atención. ROJO es para cuando FALTAN DATOS DE ALGUNA PARTE.»
   *
   * Lo que el bot escribió y nadie cotejó es exactamente «el bot no está
   * seguro»: la fecha es la del día que la puso y el remitente es una
   * deducción del padrón. Guardar limpia las dos marcas y el CRT queda
   * verde — completo y mirado. */
  return numeroSinMirar(crt) || elegidoSinMirar(crt) ? "amarillo" : "verde";
}

/* ── EL CRT QUE SE ARMÓ CON EL TRÁNSITO SOLO ────────────────────────────────
 *
 * Fabián Vera, 06/10: *«En algunos casos, pocos, sólo recibimos el documento
 * ARCA. Por favor emitir CRT, completando todos los campos que se puedan, de
 * acuerdo al documento de ARCA»*. Desde entonces el bot arma el borrador con
 * el tránsito aunque el BL no llegue (`aduana_crt.borradores`).
 *
 * Ese borrador se ve igual que uno armado con los dos papeles, y no lo es: la
 * mercadería es la del tránsito —por rubro, no el detalle—, y los contenedores
 * y el volumen, que sólo trae el BL, están vacíos. Hay que DECIRLO, porque
 * quien lo abre no tiene por qué saber que el BL no vino.
 *
 * NO CAMBIA EL COLOR. El semáforo sigue hablando de qué falta y de qué dudar;
 * esto es contexto para cotejar, y si el BL llega después el bot lo pega al
 * mismo borrador y el aviso se apaga solo (ya hay un `bl` entre los papeles).
 */
export const AVISO_SOLO_TRANSITO =
  "Armado sólo con el tránsito de ARCA: el BL no llegó. La mercadería es la " +
  "del tránsito (por rubro) y los contenedores y el volumen van a mano; si " +
  "una parte no está en el padrón, elegila a mano.";

export function armadoSoloConElTransito(papeles) {
  const lista = Array.isArray(papeles) ? papeles.filter(Boolean) : [];
  const tipos = lista.map((p) => String(p?.tipo || "").trim().toLowerCase());
  return tipos.includes("arca") && !tipos.includes("bl");
}

/* Todo lo que hay para mirar antes de emitir, junto. El número sin confirmar
 * va PRIMERO porque es el único que aparece siempre. */
export function queMirarAntesDeEmitir(crt, papeles) {
  const avisos = [];
  if (numeroSinMirar(crt)) {
    avisos.push("Confirmá el número: el que está lo propuso el bot.");
  }
  /* UN aviso, no uno por campo: esta lista se lee en la fila, y siete
   * renglones de motivo taparían la duda de los papeles, que es la grave.
   * El motivo de cada campo está adentro, en el cartel de «esto lo eligió
   * el bot». */
  const puestos = Object.keys(crt?.propuesto || {}).map(
    (campo) => NOMBRE_PUESTO[campo] || campo,
  );
  if (puestos.length === 1) {
    avisos.push(`El bot completó ${puestos[0]}: cotejalo contra el papel.`);
  } else if (puestos.length > 1) {
    const ultimos = puestos.pop();
    avisos.push(
      `El bot completó ${puestos.join(", ")} y ${ultimos}: ` +
      "cotejalos contra el papel.",
    );
  }
  if (armadoSoloConElTransito(papeles)) avisos.push(AVISO_SOLO_TRANSITO);
  return avisos.concat(revisarPapeles(crt, papeles));
}

export const comoSemaforo = (c) => SEMAFORO_CRT[String(c || "").trim()] || null;

/* ══ Los campos 16, 17 y 23, que se deducen en vez de tipearse ═════════════
 *
 * Fabián Vera, 06/10, para sacar carga a mano y errores de tipeo:
 *
 *   «Campo 17 debe declarar el número de Tránsito ARCA.»
 *   «Campo 16 debe repetir el mismo valor del campo 14. Valor FOB total del
 *    documento ARCA.»
 *   «Campo 23 debe tener un sello digital, teniendo en cuenta la empresa que
 *    emite el CRT. […] Se puede deducir del campo 3, que es la empresa
 *    emisora.»
 *
 * Los tres se escribían a mano y quedaban desparejos: el 17 decía a veces
 * «26 001 TRM4 008327 B», a veces «TRM4 008172 W» o «TRM4008325»; el 16
 * quedaba vacío; el 23 decía «S.R.L.», «SRL» o nada. Ninguno de los tres es
 * un dato que haya que pensar: los tres ya están en otro lado —el papel del
 * tránsito, la casilla 14 y la casilla 3—, y copiar a mano lo que ya está es
 * exactamente donde aparece el error de tipeo.
 *
 * ── LO QUE ESCRIBIÓ UNA PERSONA SE RESPETA ────────────────────────────────
 *
 * Es el mismo trato que el número del talonario (`elNumeroEsDelSistema`): se
 * propone cuando el campo está vacío o tiene lo que el sistema había puesto,
 * y no se pisa lo que alguien escribió a sabiendas. El que carga puede tener
 * en la mano un papel que dice otra cosa, y pisarle lo que tipeó sería peor
 * que dejarlo mal. */

/* ── El campo 17: el número del tránsito ARCA ──────────────────────────────
 *
 * El tránsito es el papel de `tipo = 'arca'` entre los del CRT, y su lectura
 * trae `documento_numero` tal como está impreso: «26 001 TRM4 008325 W». Si
 * la lectura no lo trajo, queda la `referencia` del papel —la que se sacó del
 * mail—, que es el mismo número dicho más corto. Sin tránsito, nada: un
 * número inventado en la casilla 17 es peor que la casilla vacía, que por lo
 * menos se ve.
 *
 * Los espacios se dejan de a uno y en mayúscula. Es la forma en que lo
 * imprime ARCA y la que se busca después en el sistema: «TRM4008325» pegado y
 * «trm4 008325» son el mismo tránsito escrito de dos maneras, y dos maneras de
 * escribir lo mismo es justo lo que Fabián pidió que deje de pasar. */
const comoTransito = (v) =>
  typeof v === "string" || typeof v === "number"
    ? String(v).replace(/\s+/g, " ").trim().toUpperCase()
    : "";

export function numeroDeTransito(papeles) {
  const arca = (Array.isArray(papeles) ? papeles : []).filter(
    (p) => String(p?.tipo || "").trim().toLowerCase() === "arca",
  );
  /* Primero el número que se leyó DEL PAPEL, en cualquiera de los tránsitos;
     recién si ninguno lo tiene, la referencia. Recorrerlos de a uno —número o
     referencia del primero, después del segundo— haría que la referencia corta
     de uno le gane al número completo del otro sólo por el orden en que
     vinieron de la base. */
  for (const p of arca) {
    const n = comoTransito(p.documento_numero);
    if (n) return n;
  }
  for (const p of arca) {
    const n = comoTransito(p.referencia);
    if (n) return n;
  }
  return "";
}

/* Si lo que hay en la casilla 17 se puede reemplazar por el tránsito.
 *
 * Vacía, claro. Y también cuando empieza con «BL »: es lo que ponía el bot
 * antes de este pedido —el número del conocimiento marítimo—, que es un
 * papel anexo de verdad pero no el que la aduana quiere ver declarado ahí. No
 * lo escribió una persona; lo escribió una regla que cambió. */
export function anexosSinTransito(texto) {
  const t = String(texto ?? "").trim();
  return t === "" || /^BL\s/i.test(t);
}

/* Lo que va en la casilla 17: el tránsito, si la casilla está libre y hay uno
 * entre los papeles; si no, lo que diga el CRT, tal cual. */
export function documentosAnexos(crt, papeles) {
  const propio = crt?.documentos_anexos ?? "";
  if (!anexosSinTransito(propio)) return propio;
  return numeroDeTransito(papeles) || propio;
}

/* ── El campo 16: repite el 14 ─────────────────────────────────────────────
 *
 * La declaración del valor de las mercancías es el mismo valor FOB del
 * tránsito que ya está en la casilla 14. Dos casillas que dicen lo mismo y se
 * tipean por separado terminan diciendo cosas distintas —o una vacía, que es
 * lo que pasaba—.
 *
 * `mismoValor` compara como NÚMERO cuando los dos lo son: el valor que vuelve
 * de la base es `122475.37` (número) y el que está en el input es
 * `"122475.37"` (texto), y son el mismo. Se leen con `unNumeroDeMaquina` y no
 * con `aNumero`: los dos vienen del input `type=number` o de la base, o sea con
 * punto decimal — ver la nota de `unNumeroDeMaquina`. */
const ESPEJO_DEL_14 = {
  valor_monto: "declaracion_valor",
  valor_moneda: "declaracion_moneda",
};

function mismoValor(a, b) {
  const x = comoTexto(a);
  const y = comoTexto(b);
  if (x === y) return true;
  const nx = unNumeroDeMaquina(x);
  const ny = unNumeroDeMaquina(y);
  return nx !== null && ny !== null && nx === ny;
}

/* El formulario después de cambiar `campo`. Si es el valor o la moneda de la
 * casilla 14, la 16 lo sigue — pero sólo si estaba vacía o decía lo mismo que
 * la 14 antes del cambio. Si decía otra cosa, alguien la escribió a propósito
 * y se deja. Cualquier otro campo pasa sin tocar nada más. */
export function siguiendoAlValor(form, campo, valor) {
  const f = form || {};
  const nuevo = { ...f, [campo]: valor };
  const espejo = ESPEJO_DEL_14[campo];
  if (!espejo) return nuevo;
  if (comoTexto(f[espejo]) === "" || mismoValor(f[espejo], f[campo])) {
    nuevo[espejo] = valor;
  }
  return nuevo;
}

/* Lo que va impreso en la casilla 16: lo declarado, y si no se declaró nada,
 * el valor y la moneda de la 14. `valor: null` es «no hay nada que poner»: la
 * casilla queda en blanco, sin una moneda suelta que parezca un dato.
 *
 * Antes la hoja sólo miraba null/undefined, y un `""` —que es como queda el
 * campo en el formulario— imprimía «U$S» solo en la casilla. */
export function declaracionDelValor(crt) {
  if (comoTexto(crt?.declaracion_valor) !== "") {
    return {
      valor: crt.declaracion_valor,
      moneda: comoTexto(crt.declaracion_moneda) || comoTexto(crt.valor_moneda),
    };
  }
  if (comoTexto(crt?.valor_monto) !== "") {
    return {
      valor: crt.valor_monto,
      moneda: comoTexto(crt.valor_moneda) || comoTexto(crt.declaracion_moneda),
    };
  }
  return { valor: null, moneda: "" };
}

/* ── El campo 23: el sello de la empresa emisora ───────────────────────────
 *
 * «Si el CRT se emite por TRANSPORTES CANTARINI, en el campo 23 debe decir
 * TRANSPORTES CANTARINI SRL.» La empresa emisora es el porteador de la
 * casilla 3, así que el sello sale de ahí cuando `firma_porteador` está vacío;
 * si alguien escribió otra cosa —un representante, otra razón social—, se usa
 * lo escrito.
 *
 * ── EL TIPO SOCIETARIO SE ESCRIBE DE UNA SOLA MANERA ──────────────────────
 *
 * Los CRT salían con «S.R.L.», «S.R.L» y «SRL» según quién lo tipeara, y el
 * padrón trae «S.R.L.». Un sello es justamente lo que dice siempre lo mismo,
 * así que el final se unifica como lo escribió Fabián en el pedido: «SRL» (y
 * «SA» por la misma regla). Sólo al FINAL del nombre: un «S.A.» en el medio de
 * una razón social es parte del nombre, no el tipo de sociedad. Y separado por
 * un espacio o una coma: «EMPRESA» termina en «SA» y no es una anónima. Lo
 * guardado en la base no se toca; esto es cómo se dibuja. */
const TIPO_SOCIETARIO = [
  [/(?:\s+|\s*,\s*)S\.?\s*R\.?\s*L\.?$/, " SRL"],
  [/(?:\s+|\s*,\s*)S\.?\s*A\.?$/, " SA"],
];

export function textoDelSello(firma, porteador) {
  const base =
    String(firma ?? "").trim() || String(porteador?.razon_social ?? "").trim();
  let t = base.replace(/\s+/g, " ").toUpperCase();
  if (!t) return "";
  for (const [como, queda] of TIPO_SOCIETARIO) {
    if (como.test(t)) { t = t.replace(como, queda).trim(); break; }
  }
  return t;
}
